import { useState } from 'react'
import { useParams } from 'react-router-dom'
import type { MenuItem } from '../api/types'
import { useRestaurantMenu } from '../hooks/useRestaurantMenu'
import { useBranding } from '../hooks/useBranding'
import { MenuHeader } from '../components/menu/MenuHeader'
import { CategoryNav } from '../components/menu/CategoryNav'
import { CategorySection } from '../components/menu/CategorySection'
import { ItemSheet } from '../components/menu/ItemSheet'
import { CartBar } from '../components/cart/CartBar'
import { CartDrawer } from '../components/cart/CartDrawer'
import { NotFoundError } from '../api/client'

export default function MenuPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: menu, isPending, error, refetch } = useRestaurantMenu(slug ?? '')
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null)
  const [cartOpen, setCartOpen] = useState(false)

  useBranding(menu)

  if (isPending) {
    return (
      <div className="min-h-screen bg-white animate-pulse" aria-busy="true" aria-label="Loading menu">
        {/* Header skeleton */}
        <div className="h-[60px] bg-neutral-200 w-full" />
        {/* Nav skeleton */}
        <div className="h-[48px] bg-white border-b border-neutral-100 w-full flex gap-2 px-4 items-center">
          {[80, 96, 64, 112].map((w) => (
            <div key={w} className="h-7 rounded-full bg-neutral-100 shrink-0" style={{ width: w }} />
          ))}
        </div>
        {/* Cards skeleton */}
        <div className="flex flex-col gap-3 px-4 py-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-neutral-100 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    const isNotFound = error instanceof NotFoundError
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <p className="text-5xl" aria-hidden="true">{isNotFound ? '🍽️' : '⚠️'}</p>
        <h1 className="text-xl font-bold text-neutral-800">
          {isNotFound ? 'Menu not found' : 'Something went wrong'}
        </h1>
        <p className="text-sm text-neutral-500">
          {isNotFound
            ? `We couldn't find a menu for "${slug}".`
            : 'Please try again in a moment.'}
        </p>
        {!isNotFound && (
          <button
            onClick={() => void refetch()}
            className="mt-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700 transition-colors"
          >
            Retry
          </button>
        )}
      </main>
    )
  }

  return (
    <>
      <div className="min-h-screen flex flex-col bg-bg-page">
        <MenuHeader menu={menu} />
        <CategoryNav categories={menu.categories} />

        <main className="flex-1">
          {menu.categories.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-neutral-400 dark:text-neutral-500">
              No menu categories yet.
            </p>
          ) : (
            menu.categories.map((cat) => (
              <CategorySection key={cat.id} category={cat} onItemTap={setActiveItem} />
            ))
          )}
        </main>

        <footer className="py-4 text-center">
          <a
            href="https://kaizen.tech"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-neutral-400 dark:text-neutral-600 hover:underline"
          >
            Powered by Kaizen Tech
          </a>
        </footer>
      </div>

      <ItemSheet item={activeItem} onClose={() => setActiveItem(null)} />
      <CartBar onOpen={() => setCartOpen(true)} primaryColor={menu.primary_color} />
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  )
}
